
import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { PredictionModel, Symbol, BacktestSettingsType } from '../types';
import { Button } from '@/components/ui/button';
import { ModelParameters } from './ModelParameters';
import { MODEL_TYPES } from '../constants';
import { AccordionTrigger, AccordionContent } from '../model-selection/Accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ModelItemProps {
  model: PredictionModel;
  index: number;
  isAccordionOpen: boolean;
  onRemoveModel: (index: number) => void;
  onModelTypeChange: (index: number, type: string) => void;
  onParameterChange: (index: number, parameter: string, value: any) => void;
  onToggleAccordion: (index: number) => void;
  canRemove: boolean;
  /** symbols for backtest context */
  symbols: Symbol[];
  /** backtest settings context */
  backtestSettings: BacktestSettingsType;
}

export const ModelItem: React.FC<ModelItemProps> = ({
  model,
  index,
  isAccordionOpen,
  onRemoveModel,
  onModelTypeChange,
  onParameterChange,
  onToggleAccordion,
  canRemove,
  symbols,
  backtestSettings
}) => {
  return (
    <div className="bg-gray-50 rounded-xl p-3 flex flex-col">
      <div className="flex items-center gap-2">
        <Select
          value={model.type}
          onValueChange={(value) => onModelTypeChange(index, value)}
        >
          <SelectTrigger className="rounded-xl flex-1 w-full">
            <SelectValue placeholder="Select model type">
              {MODEL_TYPES.find(mt => mt.id === model.type)?.name || model.type}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {MODEL_TYPES.map((modelTypeOption) => (
              <SelectItem key={modelTypeOption.id} value={modelTypeOption.id}>
                {modelTypeOption.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {canRemove && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => onRemoveModel(index)}
            className="rounded-xl"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="mt-2">
        <AccordionTrigger
          onClick={() => onToggleAccordion(index)}
          isOpen={isAccordionOpen}
        >
          <span className="text-xs font-medium">Advanced Parameters</span>
        </AccordionTrigger>
        {isAccordionOpen && (
          <AccordionContent isOpen={isAccordionOpen}>
            <ModelParameters
              model={model}
              onModelChange={(changes) => {
                for (const [key, value] of Object.entries(changes)) {
                  onParameterChange(index, key, value);
                }
              }}
              symbols={symbols}
              backtestSettings={backtestSettings}
            />
          </AccordionContent>
        )}
      </div>
    </div>
  );
};
